import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api, { notifyUpdate } from '../../lib/api';
import { CITIES, IMG } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, CheckCircle2 } from 'lucide-react';

const fmtDate = (d) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${day}.${m}.${y}.`; };
const CATEGORIES = ['Kulturna manifestacija', 'Sportska manifestacija', 'Festival', 'Gastro manifestacija', 'Vjerska manifestacija', 'Ostalo'];
const empty = { title: '', city: '', location: '', date: '', endDate: '', time: '10:00', category: 'Kulturna manifestacija', description: '', image: IMG.fortress, lat: 44.15, lng: 17.65 };

export default function AdminDogadjaji() {
  const { user, hasPermission } = useAuth();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const canManage = hasPermission('manage_events');
  const canApprove = hasPermission('approve_events');

  useEffect(() => {
    const load = () => api.get('/events/all').then((res) => setEvents(res.data)).catch(() => {});
    load();
    window.addEventListener('store-updated', load);
    return () => window.removeEventListener('store-updated', load);
  }, []);

  const visible = user?.role === 'sluzbenik' ? events.filter((e) => e.city === user.city) : events;

  const openNew = () => {
    setForm({ ...empty, city: user?.role === 'sluzbenik' ? user.city : '' });
    setEditId(null); setOpen(true);
  };
  const openEdit = (e) => { setForm({ ...empty, ...e }); setEditId(e.id); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.city || !form.date || !form.description) { toast.error('Popunite naziv, grad, datum i opis.'); return; }
    const payload = { ...form, endDate: form.endDate || form.date, lat: parseFloat(form.lat), lng: parseFloat(form.lng) };
    try {
      if (editId) {
        await api.put(`/events/${editId}`, payload);
        toast.success('Događaj je ažuriran.');
      } else {
        const { data: created } = await api.post('/events', payload);
        toast.success(created.status === 'approved' ? 'Događaj je objavljen.' : 'Događaj je poslan administratoru na odobrenje.');
      }
      notifyUpdate();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri spremanju.');
    }
  };

  const approve = async (id) => {
    try {
      await api.put(`/events/${id}/approve`);
      toast.success('Događaj je odobren i objavljen.');
      notifyUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri odobravanju.');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success('Događaj je obrisan.');
      notifyUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Greška pri brisanju.');
    }
  };

  return (
    <div data-testid="admin-dogadjaji">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0b1a3d]">Događaji</h1>
          <p className="text-sm text-slate-500">{canManage ? 'Upravljanje događajima i kalendarom.' : 'Pregled događaja.'}</p>
        </div>
        {canManage && (
          <Button onClick={openNew} className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md transition-colors duration-200" data-testid="add-event-btn">
            <Plus className="h-4 w-4 mr-2" /> Novi događaj
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#f8f9fd]">
              <TableHead className="text-[#001752] font-semibold">Naziv</TableHead>
              <TableHead className="text-[#001752] font-semibold w-32">Datum</TableHead>
              <TableHead className="text-[#001752] font-semibold w-40 hidden md:table-cell">Grad/Općina</TableHead>
              <TableHead className="text-[#001752] font-semibold w-28">Status</TableHead>
              {canManage && <TableHead className="w-32"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((e) => (
              <TableRow key={e.id} data-testid={`event-row-${e.id}`}>
                <TableCell className="font-medium text-slate-700 max-w-xs truncate">{e.title}</TableCell>
                <TableCell className="text-slate-500">{fmtDate(e.date)}</TableCell>
                <TableCell className="text-slate-500 hidden md:table-cell">{e.city}</TableCell>
                <TableCell>
                  {e.status === 'approved'
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Objavljen</Badge>
                    : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Na čekanju</Badge>}
                </TableCell>
                {canManage && (
                  <TableCell>
                    <div className="flex gap-1.5 justify-end">
                      {e.status === 'pending' && canApprove && (
                        <Button size="icon" variant="outline" onClick={() => approve(e.id)} data-testid={`approve-event-${e.id}`}
                          className="h-8 w-8 border-slate-200 text-emerald-600 hover:bg-emerald-600 hover:text-white rounded-md transition-colors duration-200">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="icon" variant="outline" onClick={() => openEdit(e)} data-testid={`edit-event-${e.id}`}
                        className="h-8 w-8 border-slate-200 text-[#001752] hover:bg-[#001752] hover:text-white rounded-md transition-colors duration-200">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="outline" onClick={() => remove(e.id)} data-testid={`delete-event-${e.id}`}
                        className="h-8 w-8 border-slate-200 text-red-600 hover:bg-red-600 hover:text-white rounded-md transition-colors duration-200">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="event-dialog-form">
          <DialogHeader>
            <DialogTitle className="text-[#0b1a3d]">{editId ? 'Uredi događaj' : 'Novi događaj'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 mb-1.5 block">Naziv događaja *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-form-title" className="border-slate-200" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600 mb-1.5 block">Grad/Općina *</Label>
                <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })} disabled={user?.role === 'sluzbenik'}>
                  <SelectTrigger className="border-slate-200" data-testid="event-form-city"><SelectValue placeholder="Odaberite" /></SelectTrigger>
                  <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Lokacija</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-form-location" className="border-slate-200" />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-600 mb-1.5 block">Datum početka *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-form-date" className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Datum završetka</Label>
                <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">Vrijeme</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Kategorija</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">Opis *</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-form-description" className="border-slate-200" />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-1">
                <Label className="text-slate-600 mb-1.5 block">URL slike</Label>
                <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">GPS širina (lat)</Label>
                <Input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="border-slate-200" />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">GPS dužina (lng)</Label>
                <Input type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="border-slate-200" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-200">Odustani</Button>
            <Button onClick={save} className="bg-[#001752] hover:bg-[#0a2a7a] text-white transition-colors duration-200" data-testid="event-form-save">Sačuvaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
